/**
 * Carrinho Lateral V6 (aprimoramento progressivo — sem JS, o link do
 * carrinho navega para /cart e os forms de produto seguem o submit nativo).
 *
 * - Intercepta somente forms do tema ([data-cart-add-form]) quando o
 *   submitter é o botão normal ([data-cart-add-button]) — nunca
 *   payment_button, checkout ou forms de app;
 * - Cart Ajax API + Bundled Section Rendering: 1 requisição por mutação;
 * - cart/change com item.key (nunca cart/update para linhas);
 * - fila serial (sem AbortController em POST: abortar o fetch não desfaz a
 *   mutação no servidor) + debounce por linha, só a última quantidade;
 * - troca apenas de [data-cart-drawer-root]; nenhum listener por linha.
 */
(function () {
  'use strict';

  if (customElements.get('cart-drawer')) return;

  var QTY_DEBOUNCE_MS = 220;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cart-drawer]');
      if (!this.dialog) return;

      this.queue = Promise.resolve(); // fila serial de mutações
      this.lineDesired = new Map(); // key → última quantidade desejada
      this.lineTimers = new Map(); // key → timer de debounce
      this.pendingLines = new Set(); // keys já enfileiradas
      this.inFlightKey = null; // linha com mutação em voo
      this.trigger = null;

      this.onDocClick = this.handleDocClick.bind(this);
      this.onDocSubmit = this.handleDocSubmit.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onDialogClose = this.handleDialogClose.bind(this);

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('submit', this.onDocSubmit);
      this.addEventListener('change', this.onChange);
      this.dialog.addEventListener('close', this.onDialogClose);

      // Brinde automático: estado inicial pode já merecer o item
      this.scheduleGiftSync();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('submit', this.onDocSubmit);
      this.removeEventListener('change', this.onChange);
      this.dialog.removeEventListener('close', this.onDialogClose);
      this.lineTimers.forEach(function (timer) {
        clearTimeout(timer);
      });
    }

    /* ------------------------------ helpers ----------------------------- */

    root() {
      return this.querySelector('[data-cart-drawer-root]');
    }

    lineFor(target) {
      return target.closest('[data-cart-line]');
    }

    lineByKey(key) {
      var lines = this.querySelectorAll('[data-cart-line]');
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].dataset.lineKey === key) return lines[i];
      }
      return null;
    }

    itemsText(count) {
      if (count === 0) return this.dataset.textItemsZero || '';
      if (count === 1) return this.dataset.textItemsOne || '';
      return (this.dataset.textItemsOther || '').replace('[count]', String(count));
    }

    announce(text) {
      var live = this.querySelector('[data-cart-drawer-live]');
      if (!live) return;
      // limpa e regrava em uma tarefa separada para que anúncios repetidos
      // sejam lidos; setTimeout (não rAF) para funcionar também em aba oculta
      live.textContent = '';
      setTimeout(function () {
        live.textContent = text;
      }, 30);
    }

    showError(text) {
      var region = this.querySelector('[data-cart-drawer-error]');
      if (!region) return;
      region.textContent = text;
      region.hidden = false;
    }

    clearError() {
      var region = this.querySelector('[data-cart-drawer-error]');
      if (!region) return;
      region.textContent = '';
      region.hidden = true;
    }

    setBusy(busy) {
      if (busy) {
        this.dialog.setAttribute('aria-busy', 'true');
      } else {
        this.dialog.removeAttribute('aria-busy');
      }
    }

    /* ------------------------------ abrir/fechar ------------------------ */

    open(trigger) {
      if (trigger) this.trigger = trigger;
      if (typeof this.dialog.showModal !== 'function') return;
      if (!this.dialog.open) this.dialog.showModal();
      var close = this.querySelector('[data-cart-drawer-close]');
      if (close) close.focus();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    handleDialogClose() {
      var target = this.trigger;
      if (!target || !document.contains(target)) {
        target = document.querySelector('[data-cart-link]');
      }
      if (target && target.offsetParent !== null) target.focus();
    }

    /* ------------------------------ eventos ----------------------------- */

    handleDocClick(event) {
      var opener = event.target.closest('[data-cart-drawer-open]');
      if (opener) {
        event.preventDefault();
        this.open(opener);
        return;
      }

      var closer = event.target.closest('[data-cart-drawer-close]');
      if (closer && this.contains(closer)) {
        this.close();
        return;
      }

      // Clique no backdrop: o alvo é o próprio dialog
      if (event.target === this.dialog) {
        this.close();
        return;
      }

      var minus = event.target.closest('[data-cart-qty-minus]');
      if (minus && this.contains(minus)) {
        this.stepLine(this.lineFor(minus), -1);
        return;
      }

      var plus = event.target.closest('[data-cart-qty-plus]');
      if (plus && this.contains(plus)) {
        this.stepLine(this.lineFor(plus), 1);
        return;
      }

      var remove = event.target.closest('[data-cart-remove]');
      if (remove && this.contains(remove)) {
        var line = this.lineFor(remove);
        if (line) this.requestLineQuantity(line.dataset.lineKey, 0, 'remove');
      }
    }

    handleDocSubmit(event) {
      var form = event.target;
      if (!form.matches('[data-cart-add-form]')) return;

      // Intercepta somente o botão normal de adicionar — nunca
      // payment_button, checkout ou forms de app.
      var submitter = event.submitter;
      if (!submitter || !submitter.matches('[data-cart-add-button]')) return;

      event.preventDefault();
      this.addFromForm(form, submitter);
    }

    handleChange(event) {
      var input = event.target;
      if (!input.matches('[data-cart-qty-input]')) return;
      var line = this.lineFor(input);
      if (!line) return;

      var min = parseInt(input.dataset.ruleMin, 10) || 1;
      var step = parseInt(input.dataset.ruleStep, 10) || 1;
      var max = input.dataset.ruleMax ? parseInt(input.dataset.ruleMax, 10) : Infinity;
      var value = parseInt(input.value, 10);

      if (isNaN(value) || value < 0) value = min;
      if (value === 0) {
        // quantidade 0 remove a linha
        this.requestLineQuantity(line.dataset.lineKey, 0, 'input');
        return;
      }
      if (value < min) value = min;
      if (value > max) value = max;
      value = min + Math.round((value - min) / step) * step;
      if (value > max) value -= step;
      if (value < min) value = min;
      input.value = value;

      this.requestLineQuantity(line.dataset.lineKey, value, 'input');
    }

    stepLine(line, direction) {
      if (!line || this.inFlightKey === line.dataset.lineKey) return;
      var input = line.querySelector('[data-cart-qty-input]');
      if (!input) return;
      var min = parseInt(input.dataset.ruleMin, 10) || 1;
      var step = parseInt(input.dataset.ruleStep, 10) || 1;
      var max = input.dataset.ruleMax ? parseInt(input.dataset.ruleMax, 10) : Infinity;
      var current = this.lineDesired.has(line.dataset.lineKey)
        ? this.lineDesired.get(line.dataset.lineKey)
        : parseInt(input.value, 10) || min;

      var next = current + direction * step;
      if (next < min) return; // não desce abaixo do mínimo (remover é explícito)
      if (next > max) next = max;
      input.value = next;

      var type = direction < 0 ? 'minus' : 'plus';
      this.requestLineQuantity(line.dataset.lineKey, next, type);
    }

    /* --------------------------- fila e mutações ------------------------ */

    enqueue(task) {
      this.queue = this.queue.then(task).catch(function () {});
    }

    // Debounce por linha: registra a quantidade desejada e agenda uma única
    // mutação; a execução lê sempre o último valor desejado da linha.
    requestLineQuantity(key, quantity, focusType) {
      this.lineDesired.set(key, quantity);
      this.lastFocus = { type: focusType, key: key };

      clearTimeout(this.lineTimers.get(key));
      var self = this;
      this.lineTimers.set(
        key,
        setTimeout(function () {
          self.lineTimers.delete(key);
          if (self.pendingLines.has(key)) return; // mutação já enfileirada lerá o valor novo
          self.pendingLines.add(key);
          self.enqueue(function () {
            self.pendingLines.delete(key);
            var desired = self.lineDesired.get(key);
            return self.performChange(key, desired);
          });
        }, QTY_DEBOUNCE_MS)
      );
    }

    performChange(key, quantity) {
      var self = this;
      this.inFlightKey = key;
      this.setBusy(true);
      var line = this.lineByKey(key);
      if (line) line.classList.add('is-loading');

      return fetch(this.dataset.cartChangeUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: this.dataset.sectionId,
          sections_url: this.dataset.cartUrl
        })
      })
        .then(function (response) {
          return response.json().then(function (json) {
            return { ok: response.ok, json: json };
          });
        })
        .then(function (result) {
          if (!self.isConnected) return;
          if (!result.ok || result.json.status) {
            self.showError(result.json.description || result.json.message || self.dataset.textError);
            var stale = self.lineByKey(key);
            if (stale) stale.classList.remove('is-loading');
            return;
          }
          self.clearError();
          self.renderSections(result.json.sections);
          var text = quantity === 0 ? self.dataset.textRemoved : self.dataset.textUpdated;
          self.announce((text || '') + ' ' + self.itemsText(self.currentCount()));
        })
        .catch(function () {
          if (!self.isConnected) return;
          self.showError(self.dataset.textError || '');
          var stale = self.lineByKey(key);
          if (stale) stale.classList.remove('is-loading');
        })
        .finally(function () {
          self.inFlightKey = null;
          if (self.isConnected) self.setBusy(false);
        });
    }

    addFromForm(form, submitter) {
      var self = this;
      var body = new FormData(form);
      body.append('sections', this.dataset.sectionId);
      body.append('sections_url', this.dataset.cartUrl);

      this.enqueue(function () {
        self.setBusy(true);
        submitter.setAttribute('aria-busy', 'true');

        return fetch(self.dataset.cartAddUrl + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: body
        })
          .then(function (response) {
            return response.json().then(function (json) {
              return { ok: response.ok, json: json };
            });
          })
          .then(function (result) {
            if (!self.isConnected) return;
            if (!result.ok || result.json.status) {
              self.showError(result.json.description || result.json.message || self.dataset.textError);
              self.open(submitter);
              return;
            }
            self.clearError();
            self.renderSections(result.json.sections);
            self.lastFocus = null; // foco vai para o botão fechar via open()
            self.open(submitter);
            var title = result.json.product_title || (result.json.items && result.json.items[0] && result.json.items[0].product_title) || '';
            self.announce(
              (self.dataset.textAdded || '').replace('[title]', title) + ' ' + self.itemsText(self.currentCount())
            );
          })
          .catch(function () {
            // Sem fallback para submit nativo: o servidor pode já ter
            // processado a adição — repetir duplicaria a linha.
            if (!self.isConnected) return;
            self.showError(self.dataset.textError || '');
            self.open(submitter);
          })
          .finally(function () {
            submitter.removeAttribute('aria-busy');
            if (self.isConnected) self.setBusy(false);
          });
      });
    }

    /* --------------------------- renderização --------------------------- */

    currentCount() {
      var root = this.root();
      return root ? parseInt(root.dataset.itemCount, 10) || 0 : 0;
    }

    renderSections(sections) {
      var html = sections ? sections[this.dataset.sectionId] : null;
      if (!html) {
        // Fallback: busca somente esta section via Section Rendering API
        if (window.console && console.debug) {
          console.debug('[cart-drawer] sections ausente na resposta; usando fallback de section');
        }
        return this.refreshFromSectionApi();
      }
      this.swapRoot(html);
    }

    refreshFromSectionApi() {
      var self = this;
      var url = this.dataset.cartUrl + '?section_id=' + encodeURIComponent(this.dataset.sectionId);
      return fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          if (self.isConnected) self.swapRoot(html);
        })
        .catch(function () {
          if (self.isConnected) self.showError(self.dataset.textError || '');
        });
    }

    swapRoot(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.querySelector('[data-cart-drawer-root]');
      var current = this.root();
      if (!next || !current) return;

      // Contexto de foco antes da troca (tipo de controle + linha)
      var focusContext = this.lastFocus;
      var previousIndex = -1;
      if (focusContext) {
        var lines = Array.prototype.slice.call(this.querySelectorAll('[data-cart-line]'));
        for (var i = 0; i < lines.length; i++) {
          if (lines[i].dataset.lineKey === focusContext.key) previousIndex = i;
        }
      }

      current.replaceWith(next);
      this.updateCounts();
      if (focusContext) this.restoreFocus(focusContext, previousIndex);
      this.lastFocus = null;

      // Toda mutação pode cruzar (ou perder) a meta do brinde
      this.scheduleGiftSync();
    }

    /* --------------------------- brinde automático ----------------------- */

    // Adiciona o brinde quando o carrinho (sem contar o próprio brinde)
    // cruza a meta e remove quando cai abaixo — mas SÓ mexe na linha se o
    // desconto do Admin zerou o preço; um brinde comprado por valor cheio é
    // uma linha normal do cliente e nunca é removida daqui.
    scheduleGiftSync() {
      var giftVariant = parseInt(this.dataset.giftVariant, 10);
      var giftThreshold = parseInt(this.dataset.giftThreshold, 10);
      if (!giftVariant || !(giftThreshold > 0)) return;
      if (this.giftSyncQueued) return;
      this.giftSyncQueued = true;

      var self = this;
      this.enqueue(function () {
        self.giftSyncQueued = false;
        return self.syncGift(giftVariant, giftThreshold);
      });
    }

    syncGift(giftVariant, giftThreshold) {
      var self = this;
      return fetch(this.dataset.cartUrl + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then(function (cart) {
          if (!self.isConnected) return;

          var giftLine = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].variant_id === giftVariant) {
              giftLine = cart.items[i];
              break;
            }
          }

          var giftIsFree = giftLine && giftLine.final_line_price === 0;
          var base = cart.total_price - (giftLine ? giftLine.final_line_price : 0);

          if (base >= giftThreshold && !giftLine) {
            return self.mutateGift(self.dataset.cartAddUrl + '.js', {
              items: [{ id: giftVariant, quantity: 1 }],
              sections: self.dataset.sectionId,
              sections_url: self.dataset.cartUrl
            });
          }
          if (base < giftThreshold && giftIsFree) {
            return self.mutateGift(self.dataset.cartChangeUrl + '.js', {
              id: giftLine.key,
              quantity: 0,
              sections: self.dataset.sectionId,
              sections_url: self.dataset.cartUrl
            });
          }
        })
        .catch(function () {
          // silencioso: o brinde é cortesia, nunca pode travar o carrinho
        });
    }

    mutateGift(url, payload) {
      var self = this;
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.json().then(function (json) {
            return { ok: response.ok, json: json };
          });
        })
        .then(function (result) {
          if (!self.isConnected || !result.ok || result.json.status) return;
          self.renderSections(result.json.sections);
        })
        .catch(function () {});
    }

    restoreFocus(context, previousIndex) {
      var selectorByType = {
        minus: '[data-cart-qty-minus]',
        plus: '[data-cart-qty-plus]',
        input: '[data-cart-qty-input]',
        remove: '[data-cart-remove]'
      };
      var selector = selectorByType[context.type] || '[data-cart-qty-input]';

      // 1. mesma linha (item.key), mesmo tipo de controle
      var line = this.lineByKey(context.key);
      var target = line ? line.querySelector(selector) : null;

      // 2. linha seguinte → 3. anterior → 4. botão fechar
      if (!target) {
        var lines = this.querySelectorAll('[data-cart-line]');
        var fallbackLine = null;
        if (lines.length > 0 && previousIndex >= 0) {
          fallbackLine = lines[Math.min(previousIndex, lines.length - 1)];
        }
        if (fallbackLine) {
          target = fallbackLine.querySelector(selector) || fallbackLine.querySelector('[data-cart-qty-input]') || fallbackLine.querySelector('[data-cart-remove]');
        }
      }
      if (!target) target = this.querySelector('[data-cart-drawer-close]');
      if (target && this.dialog.open) target.focus();
    }

    updateCounts() {
      var count = this.currentCount();
      var itemsText = this.itemsText(count);
      var cartText = this.dataset.textCart || '';

      // Badge(s) do header
      var badges = document.querySelectorAll('[data-cart-count]');
      Array.prototype.forEach.call(badges, function (badge) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.hidden = count === 0;
      });

      // Nome acessível dos links de carrinho
      var labels = document.querySelectorAll('[data-cart-link-label]');
      Array.prototype.forEach.call(labels, function (label) {
        label.textContent = cartText + ' — ' + itemsText;
      });

      // Contador do título do drawer
      var drawerCount = this.querySelector('[data-cart-drawer-count]');
      if (drawerCount) drawerCount.textContent = itemsText;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
